import { clsx } from "clsx";
import { useMemo, useRef } from "react";
import { mergeProps } from "react-aria";

import { useMeasureOnResize } from "../../hooks/useMeasureOnResize.ts";
import { useTooltip } from "../../hooks/useTooltip.ts";
import { createTextWidthMeasurer } from "../../utils/string/createTextMeasurer.ts";
import { truncateListToFit, type TruncateListToFitResult } from "../../utils/string/truncate-list.ts";
import { AnimatedTooltip } from "../Tooltip/AnimatedTooltip.tsx";
import css from "./Truncate.module.css";

export interface TruncateListRenderContext<T> {
  fullText: string;
  isTruncated: boolean;
  resultText: string;
  visibleItems: readonly T[];
  visibleLabels: readonly string[];
  hiddenCount: number;
  suffixText: string;
  separator: string;
}

export interface TruncateListProps<T> {
  items: readonly T[];
  getLabel: (item: T, index: number) => string;
  children: (context: TruncateListRenderContext<T>) => React.ReactNode;
  separator?: string;
  ellipsis?: string;
  extraWidthPerVisibleItem?: number;
  className?: string;
  style?: React.CSSProperties;
}

type TruncateListDomProps = Omit<React.HTMLAttributes<HTMLSpanElement>, "children">;

export function TruncateList<T>({
  items,
  getLabel,
  children,
  separator = ", ",
  ellipsis = "…",
  extraWidthPerVisibleItem = 0,
  className = "",
  style,
  ...props
}: TruncateListProps<T> & TruncateListDomProps) {
  const labels = useMemo(() => items.map((item, index) => getLabel(item, index)), [items, getLabel]);
  const fullText = useMemo(() => labels.join(separator), [labels, separator]);
  const elementRef = useRef<HTMLSpanElement>(null);

  const measureText = (element: HTMLSpanElement) => {
    const containerWidth = element.clientWidth;

    if (containerWidth === 0) {
      return {
        text: fullText,
        isTruncated: false,
        visibleCount: labels.length,
        hiddenCount: 0,
        visibleItems: labels,
        suffixText: "",
      };
    }

    const measure = createTextWidthMeasurer(element);

    return truncateListToFit(labels, containerWidth, measure, {
      separator,
      ellipsis,
      extraWidthPerVisibleItem,
    });
  };

  const initialResult: TruncateListToFitResult = {
    text: fullText,
    isTruncated: false,
    visibleCount: labels.length,
    hiddenCount: 0,
    visibleItems: labels,
    suffixText: "",
  };

  const result = useMeasureOnResize(
    elementRef,
    measureText,
    [labels, fullText, separator, ellipsis, extraWidthPerVisibleItem],
    initialResult,
  );

  const { state, tooltipProps, triggerProps } = useTooltip(elementRef, { isDisabled: !result.isTruncated });
  const visibleItems = items.slice(0, result.visibleCount);
  const context: TruncateListRenderContext<T> = {
    fullText,
    isTruncated: result.isTruncated,
    resultText: result.text,
    visibleItems,
    visibleLabels: result.visibleItems,
    hiddenCount: result.hiddenCount,
    suffixText: result.suffixText,
    separator,
  };

  return (
    <>
      <span
        ref={elementRef}
        className={clsx(css.text, className)}
        style={style}
        data-testid="truncate-list"
        {...mergeProps(props, triggerProps)}
        aria-label={fullText}
      >
        {children(context)}
      </span>
      <AnimatedTooltip
        domProps={tooltipProps}
        state={state}
        targetRef={elementRef}
        title={fullText}
        nonInteractive
        offset={5}
        style={{ maxWidth: "unset" }}
      />
    </>
  );
}
